import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CalendarClock, Eraser, Zap } from "lucide-react";

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6..21

export const fmtHour = (h) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? "am" : "pm"}`;

export default function AvailabilityGrid() {
  const { user, refresh } = useAuth();
  const [slots, setSlots] = useState(new Set(user?.availability_slots || []));
  const [saving, setSaving] = useState(false);
  const [togglingFree, setTogglingFree] = useState(false);
  const drag = useRef(null);

  const freeUntil = user?.free_until;
  const isFreeNow = freeUntil && new Date(freeUntil) > new Date();
  const freeLabel = isFreeNow ? new Date(freeUntil).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
  const toggleFree = async () => {
    setTogglingFree(true);
    try { await http.post("/users/free-now"); await refresh(); }
    catch (e) { toast.error(formatError(e)); }
    setTogglingFree(false);
  };

  useEffect(() => { setSlots(new Set(user?.availability_slots || [])); }, [user?.availability_slots]);

  const dirty = JSON.stringify([...slots].sort()) !== JSON.stringify([...(user?.availability_slots || [])].sort());

  const apply = (key, on) => setSlots((prev) => { const n = new Set(prev); on ? n.add(key) : n.delete(key); return n; });
  const start = (key) => { const on = !slots.has(key); drag.current = on; apply(key, on); };
  const enter = (key) => { if (drag.current !== null) apply(key, drag.current); };
  useEffect(() => { const up = () => (drag.current = null); window.addEventListener("pointerup", up); return () => window.removeEventListener("pointerup", up); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/users/me", { availability_slots: [...slots].sort() });
      await refresh();
      toast.success(`Availability saved — ${slots.size} weekly slots. Matchmaking will favor players free at the same times.`);
    } catch (e) { toast.error(formatError(e)); }
    setSaving(false);
  };

  return (
    <section className="card p-5" data-testid="availability-section">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-lg pv-text-dark flex items-center gap-2"><CalendarClock size={18} /> Weekly availability</h2>
          <p className="text-xs text-gray-500">Tap or drag to mark when you can play. <b data-testid="slot-count">{slots.size}</b> hourly slots selected.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="clear-availability-btn" onClick={() => setSlots(new Set())} className="btn-ghost text-sm flex items-center gap-1"><Eraser size={14} /> Clear</button>
          <button data-testid="save-availability-btn" onClick={save} disabled={!dirty || saving} className="btn-primary text-sm flex items-center gap-1 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
          </button>
        </div>
      </div>
      <div className="mb-4 rounded-xl border p-3 flex items-center justify-between gap-3" data-testid="free-now-bar"
        style={{ borderColor: isFreeNow ? "#F5B700" : "rgba(0,0,0,0.08)", background: isFreeNow ? "rgba(245,183,0,0.08)" : "#fff" }}>
        <div className="flex items-center gap-2">
          <Zap size={18} className={isFreeNow ? "text-[#F5B700]" : "text-gray-400"} />
          <div>
            <div className="text-sm font-bold pv-text-dark">{isFreeNow ? "You're free to play right now" : "Free to play right now?"}</div>
            <div className="text-xs text-gray-500">{isFreeNow ? `Visible to nearby players until ${freeLabel}` : "Flip on for 2 hours to find an instant game"}</div>
          </div>
        </div>
        <button data-testid="free-now-toggle" onClick={toggleFree} disabled={togglingFree}
          className={`rounded-full px-4 py-2 text-sm font-black transition-colors shrink-0 ${isFreeNow ? "bg-[#F5B700] text-black" : "pv-bg-dark text-white"}`}>
          {togglingFree ? <Loader2 size={14} className="animate-spin" /> : isFreeNow ? "I'm done" : "I'm free now"}
        </button>
      </div>
      <div className="overflow-x-auto select-none">
        <div className="grid min-w-[560px]" style={{ gridTemplateColumns: "44px repeat(7, 1fr)", gap: 3 }}>
          <div />
          {DAYS.map((d) => <div key={d} className="text-center text-[11px] font-extrabold uppercase tracking-wider pv-text-dark py-1">{d}</div>)}
          {HOURS.map((h) => (
            <Row key={h} h={h} slots={slots} start={start} enter={enter} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Row({ h, slots, start, enter }) {
  return (
    <>
      <div className="text-[10px] text-gray-500 font-semibold text-right pr-2 leading-6">{fmtHour(h)}</div>
      {DAYS.map((_, d) => {
        const key = `${d}-${h}`;
        const on = slots.has(key);
        return (
          <button key={key} type="button" data-testid={`slot-${key}`} aria-pressed={on}
            onPointerDown={(e) => { e.preventDefault(); start(key); }} onPointerEnter={() => enter(key)}
            className={`h-6 rounded-md transition-colors border ${on ? "pv-bg-primary border-[#a5bd2c]" : "bg-[#F8F6F3] border-gray-200 hover:bg-gray-200"}`} />
        );
      })}
    </>
  );
}
